import { Badge, type CrudConfig } from '@startupquickstart/frontend';

/** Publication state of a release note. */
export type ReleaseNoteStatus = 'draft' | 'published';

/**
 * A single guided-tour step. `selector` is a CSS selector resolved against the
 * live DOM at run time; `route` is reserved for future multi-page tours (v1
 * only drives steps on the current page — see {@link useFeatureTour}).
 */
export interface TutorialStep {
  /** Optional page path the step lives on. Reserved for multi-page tours. */
  route?: string;
  /** CSS selector for the element to highlight. */
  selector: string;
  /** Popover heading shown on the highlighted element. */
  title: string;
  /** Optional popover body copy. */
  description?: string;
}

/** A viewer record attached to a release note's detail response. */
export interface ReleaseNoteView {
  id: string;
  viewedAt: string;
  user: {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    email: string;
  };
}

/**
 * Shape of a ReleaseNote as returned by the API. Release notes are repo-managed
 * content files synced into the database at API boot — they are read-only in
 * the portal (no create/edit/delete).
 */
export interface ReleaseNote {
  id: string;
  /** Stable, human-readable identifier derived from the content file. */
  slug: string;
  title: string;
  summary?: string | null;
  /** Markdown body rendered in the portal. */
  body?: string | null;
  status: ReleaseNoteStatus;
  publishedAt?: string | null;
  tutorialSteps?: TutorialStep[] | null;
  createdAt: string;
  updatedAt: string;
  /** Aggregate counts included on list rows. */
  _count?: {
    views: number;
  };
}

/** Full release note shape returned by the single-record endpoint. */
export interface ReleaseNoteWithViews extends ReleaseNote {
  views?: ReleaseNoteView[];
}

const STATUS_OPTIONS: { label: string; value: ReleaseNoteStatus }[] = [
  { label: 'Draft', value: 'draft' },
  { label: 'Published', value: 'published' },
];

/** Render a status value as a colored badge. */
export function formatReleaseNoteStatus(value: ReleaseNoteStatus | null | undefined) {
  if (!value) return <span className="text-muted-foreground">—</span>;
  const label = STATUS_OPTIONS.find((o) => o.value === value)?.label ?? value;
  const variantClass =
    value === 'published'
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-amber-100 text-amber-800 border-amber-200';
  return (
    <Badge variant="outline" className={variantClass}>
      {label}
    </Badge>
  );
}

/**
 * ReleaseNote CRUD configuration for use with CrudListView. Release notes are
 * read-only in the portal: create / edit / delete actions are disabled because
 * the content is managed as repo files synced at API boot.
 */
export const releaseNoteCrudConfig: CrudConfig<ReleaseNote> = {
  entityName: 'Release Note',
  entityNamePlural: 'Release Notes',

  endpoints: {
    list: '/release-notes',
    single: (id: string) => `/release-notes/${id}`,
    // Create / update / delete are unused — release notes are read-only in the
    // portal. Endpoints are required by CrudConfig but never invoked because the
    // corresponding list-view actions are disabled below.
    create: '/release-notes',
    update: (id: string) => `/release-notes/${id}`,
    delete: (id: string) => `/release-notes/${id}`,
  },

  fields: [
    {
      key: 'title',
      label: 'Title',
      type: 'text',
      required: true,
    },
    {
      key: 'slug',
      label: 'Slug',
      type: 'text',
      readonly: true,
      format: (value: string | null | undefined) =>
        value ? (
          <code className="text-xs text-muted-foreground">{value}</code>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'summary',
      label: 'Summary',
      type: 'textarea',
      required: false,
    },
    {
      key: 'body',
      label: 'Details',
      type: 'textarea',
      required: false,
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      options: STATUS_OPTIONS,
      format: (value: ReleaseNoteStatus | null | undefined) => formatReleaseNoteStatus(value),
    },
    {
      key: 'publishedAt',
      label: 'Published',
      type: 'date',
      readonly: true,
    },
    {
      // Virtual column: reads the aggregate view count from `_count.views`.
      key: '_count',
      label: 'Views',
      type: 'custom',
      align: 'right',
      readonly: true,
      format: (_value: ReleaseNote['_count'], record?: ReleaseNote) => (
        <span>{record?._count?.views ?? 0}</span>
      ),
    },
    {
      key: 'updatedAt',
      label: 'Updated',
      type: 'datetime',
      readonly: true,
    },
    {
      key: 'createdAt',
      label: 'Created',
      type: 'datetime',
      readonly: true,
    },
  ],

  listView: {
    fields: ['title', 'slug', 'status', 'publishedAt', '_count'],
    searchableFields: ['title'],
    sortableFields: ['title', 'status', 'publishedAt', 'createdAt', 'updatedAt'],
    defaultSort: { field: 'publishedAt', direction: 'desc' },
    itemsPerPage: 20,
    filters: [
      {
        key: 'status',
        label: 'Status',
        type: 'multiselect',
        options: STATUS_OPTIONS,
      },
    ],
    actions: {
      create: { enabled: false },
      view: { enabled: true },
      edit: { enabled: false },
      delete: { enabled: false },
    },
  },

  detailView: {
    sections: [
      {
        title: 'Overview',
        description: 'What shipped and the teaser copy',
        fields: ['title', 'slug', 'summary', 'status', 'publishedAt'],
        defaultExpanded: true,
      },
      {
        title: 'Details',
        description: 'The full write-up',
        fields: ['body'],
        defaultExpanded: true,
      },
      {
        title: 'System',
        description: 'Timestamps',
        fields: ['createdAt', 'updatedAt'],
        defaultExpanded: false,
      },
    ],
  },

  // Release notes are read-only in the portal; no form is rendered.
  form: {},
};
